/** Geometry-only placement for glass panels. All coordinates share one space. */

function rectOf({ left = 0, top = 0, width = 0, height = 0 } = {}) {
  return { left, top, width, height, right: left + width, bottom: top + height };
}

function intersects(a, b) {
  return a.left < b.right && a.right > b.left && a.top < b.bottom && a.bottom > b.top;
}

function inside(rect, bounds) {
  return (
    rect.left >= bounds.left &&
    rect.top >= bounds.top &&
    rect.right <= bounds.right &&
    rect.bottom <= bounds.bottom
  );
}

export function placeClearOf(anchorRect, panel, boundsInput, opts = {}) {
  const panelGap = Number.isFinite(opts.panelGap) ? opts.panelGap : 12;
  const inset = Number.isFinite(opts.inset) ? opts.inset : 8;
  const minWidth = Number.isFinite(opts.minWidth) ? opts.minWidth : 240;
  const anchor = rectOf(anchorRect);
  const boundsRaw = rectOf(boundsInput);
  const bounds = rectOf({
    left: boundsRaw.left + inset,
    top: boundsRaw.top + inset,
    width: Math.max(0, boundsRaw.width - inset * 2),
    height: Math.max(0, boundsRaw.height - inset * 2)
  });
  const blocked = rectOf({
    left: anchor.left - panelGap,
    top: anchor.top - panelGap,
    width: anchor.width + panelGap * 2,
    height: anchor.height + panelGap * 2
  });
  const width = Math.min(Math.max(0, panel?.width || 0), bounds.width);
  const height = Math.min(Math.max(0, panel?.height || 0), bounds.height);
  const centeredLeft = anchor.left + (anchor.width - width) / 2;
  const centeredTop = anchor.top + (anchor.height - height) / 2;
  const candidates = [
    ['right', anchor.right + panelGap, centeredTop],
    ['left', anchor.left - panelGap - width, centeredTop],
    ['below', centeredLeft, anchor.bottom + panelGap],
    ['above', centeredLeft, anchor.top - panelGap - height]
  ];

  for (const [strategy, left, top] of candidates) {
    const candidate = rectOf({ left, top, width, height });
    if (inside(candidate, bounds) && !intersects(candidate, blocked)) {
      return { left, top, width, strategy };
    }
  }

  const sideGaps = [
    { left: blocked.right, width: bounds.right - blocked.right },
    { left: bounds.left, width: blocked.left - bounds.left }
  ].sort((a, b) => b.width - a.width);
  for (const gap of sideGaps) {
    const shrunkWidth = Math.min(width, gap.width);
    if (shrunkWidth < minWidth || height > bounds.height) continue;
    const top = Math.min(Math.max(bounds.top, centeredTop), bounds.bottom - height);
    const candidate = rectOf({ left: gap.left, top, width: shrunkWidth, height });
    if (inside(candidate, bounds) && !intersects(candidate, blocked)) {
      return { left: gap.left, top, width: shrunkWidth, strategy: 'shrink' };
    }
  }

  // An edge placement is allowed only when it is still completely clear. This
  // intentionally returns none for a viewport-filling anchor.
  const edgeCandidates = [
    rectOf({ left: bounds.left, top: bounds.top, width, height }),
    rectOf({ left: bounds.right - width, top: bounds.top, width, height }),
    rectOf({ left: bounds.left, top: bounds.bottom - height, width, height }),
    rectOf({ left: bounds.right - width, top: bounds.bottom - height, width, height })
  ];
  for (const candidate of edgeCandidates) {
    if (inside(candidate, bounds) && !intersects(candidate, blocked)) {
      return { left: candidate.left, top: candidate.top, width, strategy: 'edge' };
    }
  }
  return { left: null, top: null, width, strategy: 'none' };
}

function overlapArea(a, b) {
  const w = Math.min(a.right, b.right) - Math.max(a.left, b.left);
  const h = Math.min(a.bottom, b.bottom) - Math.max(a.top, b.top);
  return w > 0 && h > 0 ? w * h : 0;
}

/**
 * Where a panel goes when a person ASKED for it and no clear room exists (SYS-8 F-6).
 *
 * `placeClearOf` refuses with 'none', and an auto-open honours that: a panel never opens over
 * its own element uninvited. But Edit… and All settings are requests, and a refused or hidden
 * panel answers nothing — beside an open rail a wide heading has no clear room on any side.
 * So this keeps the panel inside the visible bounds, on the side and at the height that cover
 * the element least. A clear placement, when there is one, always wins.
 */
export function placeVisible(anchorRect, panel, boundsInput, opts = {}) {
  const clear = placeClearOf(anchorRect, panel, boundsInput, opts);
  if (clear.strategy !== 'none') return clear;
  const inset = Number.isFinite(opts.inset) ? opts.inset : 8;
  const boundsRaw = rectOf(boundsInput);
  const bounds = rectOf({
    left: boundsRaw.left + inset,
    top: boundsRaw.top + inset,
    width: Math.max(0, boundsRaw.width - inset * 2),
    height: Math.max(0, boundsRaw.height - inset * 2)
  });
  const anchor = rectOf(anchorRect);
  const width = Math.min(Math.max(0, panel?.width || 0), bounds.width);
  const height = Math.min(Math.max(0, panel?.height || 0), bounds.height);
  const centeredTop = Math.min(
    Math.max(bounds.top, anchor.top + (anchor.height - height) / 2),
    bounds.bottom - height
  );
  let best = null;
  for (const left of [bounds.right - width, bounds.left]) {
    for (const top of [centeredTop, bounds.top, bounds.bottom - height]) {
      const covered = overlapArea(rectOf({ left, top, width, height }), anchor);
      if (!best || covered < best.covered) best = { left, top, covered };
    }
  }
  return { left: best.left, top: best.top, width, strategy: 'overlap' };
}
